import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import { Card, CardContent, CardHeader, Stack } from '@mui/material';

import { EditButtons, ProductTypeInput } from '@barboxx/admin-components';
import { ALCOHOL_SUBTYPE_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { ProductSubtype } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    productSubtype: ProductSubtype;
}

export const ProductSubtypeDetailsCard = ({ productSubtype }: Props) => {
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const mutateUser = useMutation({
        mutationKey: ['productSubtype', productSubtype.id],
        mutationFn: (data: ProductSubtype.UpdateRequest) =>
            Api.Products.Subtypes.putProductSubtype(productSubtype.id, data),
    });

    const formContext = useForm<ProductSubtype.UpdateRequest>({
        resolver: yupResolver(ALCOHOL_SUBTYPE_SCHEMA),
        defaultValues: productSubtype,
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: ProductSubtype.UpdateRequest) => {
        mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated product subtype!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating product subtype!', {
                    severity: 'error',
                });
            });
    };

    return (
        <Card>
            <CardHeader title="Alcohol Subtype Details" />
            <CardContent>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <Stack gap={2}>
                        <TextFieldElement
                            fullWidth
                            name="name"
                            disabled={!editable}
                            label="Name"
                        />
                        <TextFieldElement
                            fullWidth
                            name="description"
                            disabled={!editable}
                            label="Description"
                        />
                        <TextFieldElement
                            label="Aging Requirements"
                            name="agingRequirements"
                            fullWidth
                            disabled={!editable}
                        />
                        <TextFieldElement
                            label="Regional Requirements"
                            name="regionalRequirements"
                            fullWidth
                            disabled={!editable}
                        />
                        <TextFieldElement
                            label="Other Specifications"
                            name="otherSpecifications"
                            fullWidth
                            disabled={!editable}
                        />
                        <ProductTypeInput disabled={!editable} />
                        <CheckboxElement
                            name="isAlcoholic"
                            label="Is Alcoholic"
                            disabled={!editable}
                        />

                        <Can I="update" a="Alcohol">
                            <EditButtons
                                formContext={formContext}
                                handleCancel={handleCancel}
                                editable={editable}
                                editableActions={editableActions}
                            />
                        </Can>
                    </Stack>
                </FormContainer>
            </CardContent>
        </Card>
    );
};
